import type {
    JobItem,
    JobLogItem,
} from "@mrflark/core/hooks";
import {
    formatJobDuration,
    formatLocalTimestamp,
    formatRelativeTime,
    getJobStatusBadgeVariant,
    getLogLevelBadgeVariant,
    tryParseJSON,
} from "@mrflark/core/utils";
import {
    RotateCcw,
    X,
} from "lucide-preact";
import type {
    ComponentChildren,
} from "preact";
import {
    useState,
} from "preact/hooks";
import {
    Alert,
    Badge,
    Button,
    ConfirmModal,
    LoadingSpinner,
    Modal,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../mod.tsx";
import {
    getJobTypeLabel,
} from "./types.ts";

type JobDetailModalProps = {
    isOpen: boolean;
    job: JobItem | null;
    logs: JobLogItem[];
    loading: boolean;
    actionLoading: boolean;
    jobTypeLabels?: Record<string, string>;
    onClose: () => void;
    onRetry?: (jobId: number) => void;
    onCancel?: (jobId: number) => void;
    extraActions?: ComponentChildren;
};

export const JobDetailModal = ({
    isOpen,
    job,
    logs,
    loading,
    actionLoading,
    jobTypeLabels,
    onClose,
    onRetry,
    onCancel,
    extraActions,
}: JobDetailModalProps): ComponentChildren => {
    const [expandedLogIds, setExpandedLogIds] = useState<Set<number>>(
        new Set(),
    );
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);

    const toggleLogExpansion = (logId: number): void => {
        setExpandedLogIds((prev) => {
            const next = new Set(prev);
            if (next.has(logId)) {
                next.delete(logId);
            } else {
                next.add(logId);
            }
            return next;
        });
    };

    const handleClose = (): void => {
        setExpandedLogIds(new Set());
        setShowCancelConfirm(false);
        onClose();
    };

    const title = job
        ? `${getJobTypeLabel(job.jobType, jobTypeLabels)} #${job.id}`
        : "Job Detail";

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={handleClose}
                title={title}
                size="xl"
            >
                {loading
                    ? (
                        <div className="flex items-center justify-center py-8">
                            <LoadingSpinner />
                        </div>
                    )
                    : job
                    ? (
                        <div className="space-y-4">
                            <div className="flex items-center gap-2">
                                <Badge
                                    variant={getJobStatusBadgeVariant(
                                        job.status,
                                    )}
                                >
                                    {job.status}
                                </Badge>
                                <span className="text-sm text-text-secondary">
                                    Attempt {job.attempts} / {job.maxAttempts}
                                </span>
                            </div>

                            <div className="grid grid-cols-2 gap-3 text-sm">
                                <div>
                                    <span className="text-text-secondary">
                                        Created
                                    </span>
                                    <div className="text-text-primary">
                                        {formatLocalTimestamp(job.createdAt)}
                                    </div>
                                </div>
                                <div>
                                    <span className="text-text-secondary">
                                        Started
                                    </span>
                                    <div className="text-text-primary">
                                        {job.startedAt
                                            ? formatLocalTimestamp(
                                                job.startedAt,
                                            )
                                            : "\u2014"}
                                    </div>
                                </div>
                                <div>
                                    <span className="text-text-secondary">
                                        Completed
                                    </span>
                                    <div className="text-text-primary">
                                        {job.completedAt
                                            ? formatLocalTimestamp(
                                                job.completedAt,
                                            )
                                            : "\u2014"}
                                    </div>
                                </div>
                                <div>
                                    <span className="text-text-secondary">
                                        Duration
                                    </span>
                                    <div className="text-text-primary">
                                        {formatJobDuration(
                                            job.startedAt,
                                            job.completedAt,
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <span className="text-text-secondary">
                                        Worker
                                    </span>
                                    <div className="text-text-primary">
                                        {job.lockedBy ?? "\u2014"}
                                    </div>
                                </div>
                                <div>
                                    <span className="text-text-secondary">
                                        Scheduled For
                                    </span>
                                    <div className="text-text-primary">
                                        {job.scheduledFor
                                            ? formatLocalTimestamp(
                                                job.scheduledFor,
                                            )
                                            : "\u2014"}
                                    </div>
                                </div>
                            </div>

                            {job.status === "processing" &&
                                job.progress != null && (
                                <div>
                                    <div className="flex items-center justify-between text-sm mb-1">
                                        <span className="text-text-secondary">
                                            Progress
                                        </span>
                                        <span className="text-text-primary tabular-nums">
                                            {job.progress}%
                                        </span>
                                    </div>
                                    <div className="h-2 bg-bg-tertiary rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-accent rounded-full transition-all duration-500"
                                            style={{
                                                width: `${job.progress}%`,
                                            }}
                                        />
                                    </div>
                                </div>
                            )}

                            <div>
                                <h4 className="text-sm font-medium text-text-primary mb-1">
                                    Payload
                                </h4>
                                <pre className="bg-bg-tertiary rounded p-3 text-xs overflow-x-auto max-h-32 overflow-y-auto">
                                {tryParseJSON(job.payload)}
                                </pre>
                            </div>

                            {job.status === "completed" && job.result && (
                                <div>
                                    <h4 className="text-sm font-medium text-text-primary mb-1">
                                        Result
                                    </h4>
                                    <pre className="bg-green-500/10 border border-green-500/20 rounded p-3 text-xs overflow-x-auto max-h-32 overflow-y-auto text-green-400">
                                    {tryParseJSON(job.result)}
                                    </pre>
                                </div>
                            )}

                            {job.status === "failed" && job.errorMessage && (
                                <Alert variant="error">
                                    {job.errorMessage}
                                </Alert>
                            )}

                            {logs.length > 0 && (() => {
                                const hasMultipleAttempts = logs.some((log) =>
                                    log.attempt !== logs[0].attempt
                                );
                                return (
                                    <div>
                                        <h4 className="text-sm font-medium text-text-primary mb-1">
                                            Logs ({logs.length})
                                        </h4>
                                        <div className="max-h-96 overflow-y-auto rounded border border-bg-tertiary">
                                            <Table>
                                                <TableHeader>
                                                    <TableRow>
                                                        <TableHead className="w-24">
                                                            Time
                                                        </TableHead>
                                                        <TableHead className="w-16">
                                                            Level
                                                        </TableHead>
                                                        <TableHead>
                                                            Message
                                                        </TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {logs.map((log, index) => (
                                                        <>
                                                            {hasMultipleAttempts &&
                                                                (index === 0 ||
                                                                    log.attempt !==
                                                                        logs[
                                                                            index -
                                                                            1
                                                                        ].attempt) &&
                                                                (
                                                                    <TableRow>
                                                                        <TableCell
                                                                            colSpan={3}
                                                                            className="bg-bg-tertiary text-text-secondary text-xs font-medium py-1 px-3"
                                                                        >
                                                                            Attempt
                                                                            {" "}
                                                                            {log.attempt}
                                                                        </TableCell>
                                                                    </TableRow>
                                                                )}
                                                            <TableRow
                                                                className={log
                                                                        .data !==
                                                                        null
                                                                    ? "cursor-pointer hover:bg-bg-secondary"
                                                                    : ""}
                                                                onClick={() =>
                                                                    log.data !==
                                                                        null &&
                                                                    toggleLogExpansion(
                                                                        log.id,
                                                                    )}
                                                            >
                                                                <TableCell className="text-text-secondary text-xs">
                                                                    {formatRelativeTime(
                                                                        log.createdAt,
                                                                    )}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Badge
                                                                        variant={getLogLevelBadgeVariant(
                                                                            log.level,
                                                                        )}
                                                                    >
                                                                        {log.level}
                                                                    </Badge>
                                                                </TableCell>
                                                                <TableCell className="text-sm">
                                                                    {log.message}
                                                                    {log.data !==
                                                                            null &&
                                                                        !expandedLogIds
                                                                            .has(
                                                                                log.id,
                                                                            ) &&
                                                                        (
                                                                            <span className="text-text-secondary text-xs ml-2">
                                                                                [+data]
                                                                            </span>
                                                                        )}
                                                                </TableCell>
                                                            </TableRow>
                                                            {log.data !==
                                                                    null &&
                                                                expandedLogIds
                                                                    .has(
                                                                        log.id,
                                                                    ) &&
                                                                (
                                                                    <TableRow
                                                                        key={`${log.id}-data`}
                                                                    >
                                                                        <TableCell
                                                                            colSpan={3}
                                                                            className="p-0"
                                                                        >
                                                                            <pre className="bg-bg-tertiary p-2 text-xs overflow-x-auto">
                                                                        {typeof log.data === "string"
                                                                            ? tryParseJSON(log.data)
                                                                            : JSON.stringify(log.data, null, 2)
                                                                        }
                                                                            </pre>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                )}
                                                        </>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </div>
                                    </div>
                                );
                            })()}

                            <div className="flex justify-end gap-2 pt-2 border-t border-bg-tertiary">
                                {extraActions}
                                {onRetry && job.status === "failed" && (
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={() => onRetry(job.id)}
                                        isLoading={actionLoading}
                                    >
                                        <RotateCcw className="w-4 h-4 mr-1" />
                                        Retry
                                    </Button>
                                )}
                                {onCancel &&
                                    (job.status === "pending" ||
                                        job.status === "processing") &&
                                    (
                                        <Button
                                            variant="error"
                                            size="sm"
                                            onClick={() =>
                                                setShowCancelConfirm(true)}
                                        >
                                            <X className="w-4 h-4 mr-1" />
                                            Cancel
                                        </Button>
                                    )}
                            </div>
                        </div>
                    )
                    : null}
            </Modal>

            {job && onCancel && (
                <ConfirmModal
                    isOpen={showCancelConfirm}
                    onClose={() => setShowCancelConfirm(false)}
                    onConfirm={() => {
                        onCancel(job.id);
                        setShowCancelConfirm(false);
                    }}
                    title="Cancel Job"
                    message={`Cancel job #${job.id}? ${
                        job.status === "processing"
                            ? "The handler will complete its current work but the job won\u2019t be retried."
                            : "The job will not be processed."
                    }`}
                    variant="danger"
                    isLoading={actionLoading}
                />
            )}
        </>
    );
};
